import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

const REASON_KEYS = [
  { key: "audio_wrong",     i18n: "learner:report_reason_audio_wrong" },
  { key: "hints_wrong",     i18n: "learner:report_reason_hints_wrong" },
  { key: "audio_missing",   i18n: "learner:report_reason_audio_missing" },
  { key: "hints_missing",   i18n: "learner:report_reason_hints_missing" },
  { key: "my_answer",       i18n: "learner:report_reason_my_answer" },
  { key: "other",           i18n: "learner:report_reason_other" },
];

export function ReportModal({
  open,
  onClose,
  areaId,
  taskId,
  context,
}: {
  open: boolean;
  onClose: () => void;
  areaId: string;
  taskId: string;
  context?: Record<string, unknown>;
}) {
  const { t } = useTranslation(["common", "learner"]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(key: string) {
    setReasons((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  }

  async function submit() {
    if (submitting || reasons.length === 0) return;
    setSubmitting(true);
    try {
      await api("/api/learner/reports", {
        method: "POST",
        body: {
          area_id: areaId,
          task_id: taskId,
          reasons,
          context,
        },
      });
    } catch (err) {
      console.error("Report submit failed:", err);
    } finally {
      setSubmitting(false);
      setDone(true);
      setTimeout(() => {
        setReasons([]);
        setDone(false);
        onClose();
      }, 1400);
    }
  }

  function cancel() {
    if (submitting) return;
    setReasons([]);
    setDone(false);
    onClose();
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={cancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={cancel}
              aria-label="閉じる"
              className="absolute top-3 right-4 text-muted hover:text-primary text-2xl leading-none z-10"
            >
              ×
            </button>

            {done ? (
              <div className="p-10 text-center">
                <div className="text-5xl mb-3">🙏</div>
                <div className="text-lg font-bold text-primary">{t("learner:report_thanks")}</div>
                <p className="text-sm text-muted mt-2">{t("learner:report_thanks_sub")}</p>
              </div>
            ) : (
              <div className="p-4 sm:p-6 pt-10 space-y-1.5 sm:space-y-2">
                {REASON_KEYS.map((r) => (
                  <label key={r.key} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={reasons.includes(r.key)}
                      onChange={() => toggle(r.key)}
                      className="mt-1 w-5 h-5 accent-primary"
                    />
                    <span className="text-sm sm:text-base text-body">{t(r.i18n)}</span>
                  </label>
                ))}

                <div className="pt-3 sm:pt-4">
                  <button
                    onClick={submit}
                    disabled={submitting || reasons.length === 0}
                    className={`w-full font-bold rounded-xl sm:rounded-2xl py-3 shadow transition text-sm sm:text-base ${
                      submitting || reasons.length === 0
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    {submitting ? t("learner:report_submitting") : t("learner:report_submit")}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
